/**
 * Poseidon dashboard — explainable signal breakdown (§3.2 C).
 *
 * Shows how a final score was assembled from three *different kinds* of input:
 *
 *   1. the deterministic quantitative score — the only component that can
 *      originate a trade;
 *   2. a bounded, clamped AI research adjustment — never a signal in its own
 *      right, never able to create a trade from a NO_TRADE result;
 *   3. risk constraints applied downstream, which can reduce or veto but never
 *      enlarge beyond the configured caps.
 *
 * The panel states that last point explicitly rather than only implying it by
 * omitting the AI component from the trade decision.
 */
import type { SignalBreakdownView } from '../api/types';
import { VERDICT_LABELS } from '../lib/labels';
import { formatNumber, formatSigned } from '../lib/format';
import { AiDisclaimer, AiResearchBadge, Badge, KeyValueList, Panel } from './primitives';

function ScoreRow({
  label,
  value,
  max,
  ai = false,
  note,
}: {
  label: string;
  value: number;
  max: number;
  ai?: boolean;
  note?: string;
}): JSX.Element {
  const pct = max === 0 ? 0 : Math.max(-1, Math.min(1, value / max));
  const width = Math.abs(pct) * 50;
  return (
    <div className="score-row">
      <span className="text-muted" title={note}>
        {label} {ai && <AiResearchBadge />}
      </span>
      <span className="score-row__bar">
        {/* Zero line in the middle: negative contributions are visually negative. */}
        <span className="score-row__fill" style={{ left: '50%', width: 0, borderLeft: '1px solid var(--border-strong)' }} />
        <span
          className={`score-row__fill ${ai ? 'score-row__fill--ai' : value >= 0 ? 'score-row__fill--positive' : 'score-row__fill--negative'}`}
          style={value >= 0 ? { left: '50%', width: `${width}%` } : { right: '50%', width: `${width}%` }}
        />
      </span>
      <span className="text-mono">{formatSigned(value, 4)}</span>
    </div>
  );
}

export function SignalBreakdownPanel({ breakdown, mock = false }: { breakdown: SignalBreakdownView; mock?: boolean }): JSX.Element {
  const max = Math.max(1, Math.abs(breakdown.base_score), Math.abs(breakdown.final_score));

  return (
    <Panel
      title={`Explainable signal breakdown — ${breakdown.symbol}`}
      subtitle={`decision ${breakdown.decision_id}`}
      mock={mock}
      footnote={
        <>
          Thresholds: enter above {formatNumber(breakdown.thresholds.enter_above, 2)}, reduce below{' '}
          {formatNumber(breakdown.thresholds.reduce_below, 2)} · configured by{' '}
          <span className="text-mono">{breakdown.thresholds.configured_by}</span> · as of{' '}
          {new Date(breakdown.as_of).toLocaleTimeString(undefined, { hour12: false })}
        </>
      }
    >
      <div className="grid" style={{ gap: 14 }}>
        <div className="data-clock">
          <Badge tone={breakdown.decision === 'trade' ? 'ok' : 'muted'} large>
            {breakdown.decision === 'trade' ? 'TRADE' : 'NO TRADE'}
          </Badge>
          <span className="text-muted">{breakdown.decision_reason}</span>
        </div>

        <div className="score-stack">
          <ScoreRow
            label="Quantitative base score"
            value={breakdown.base_score}
            max={max}
            note="Deterministic strategy output computed from the closed-bar feature snapshot."
          />
          <ScoreRow
            label="AI research adjustment (bounded)"
            value={breakdown.research_adjustment}
            max={max}
            ai
            note="clamp(0.10 × research_score × confidence, −0.10, +0.10). An adjustment to an existing signal, not a signal."
          />
          <ScoreRow
            label="Event risk penalty"
            value={breakdown.event_penalty}
            max={max}
            ai
            note="0 for none/low risk, −0.15 for medium risk, REJECT for a prohibited high-severity event."
          />
          <ScoreRow label="Final score" value={breakdown.final_score} max={max} />
        </div>

        <div className="grid grid--2">
          <div>
            <div className="ribbon__label" style={{ marginBottom: 6 }}>
              Components
            </div>
            <div className="grid" style={{ gap: 8 }}>
              {breakdown.components.map((component) => (
                <div key={`${component.kind}-${component.label}`} className="disclosure">
                  <div className="data-clock">
                    <strong>{component.label}</strong>
                    <span className="text-mono">{formatSigned(component.contribution, 4)}</span>
                    {component.is_ai_derived && <AiResearchBadge />}
                    {component.clamped_to !== null && <Badge tone="warn">clamped to ±{formatNumber(component.clamped_to, 2)}</Badge>}
                  </div>
                  <div className="text-muted" style={{ marginTop: 5, fontSize: 12 }}>
                    {component.detail}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="ribbon__label" style={{ marginBottom: 6 }}>
              Risk constraints (downstream, final authority)
            </div>
            <div className="grid" style={{ gap: 8 }}>
              {breakdown.risk_constraints.map((constraint) => (
                <div key={constraint.constraint_id} className="disclosure">
                  <div className="data-clock">
                    <strong>{constraint.label}</strong>
                    <Badge
                      tone={
                        constraint.status === 'satisfied'
                          ? 'ok'
                          : constraint.status === 'binding'
                            ? 'warn'
                            : constraint.status === 'breached'
                              ? 'danger'
                              : 'muted'
                      }
                    >
                      {constraint.status}
                    </Badge>
                    {constraint.observed_value !== null && constraint.limit_value !== null && (
                      <span className="text-mono">
                        {formatNumber(constraint.observed_value, 2)} / {formatNumber(constraint.limit_value, 2)}
                        {constraint.unit !== null ? ` ${constraint.unit}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="text-muted" style={{ marginTop: 5, fontSize: 12 }}>
                    {constraint.explanation}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <KeyValueList
          items={[
            { key: 'Research can originate a trade', value: <Badge tone="ok">no — hard-coded false</Badge> },
            { key: 'Risk engine authority', value: `${VERDICT_LABELS.approve} / ${VERDICT_LABELS.reject} / ${VERDICT_LABELS.reduce} / ${VERDICT_LABELS.defer}, evaluated in the §7.2 order` },
            { key: 'AI output label', value: <span className="text-mono">{breakdown.ai_output_label}</span> },
          ]}
        />

        <AiDisclaimer />
      </div>
    </Panel>
  );
}
